import {
  ChallengeCampaignEventState,
  TaskStatus,
  ChallengeTask,
} from "@/types";
import { getMaxPoints } from "@/lib/challenges";
import { cn } from "@/helpers/cn";
import { ArrowRight } from "lucide-react";
import { QuestLogo } from "@/components/quest-logo";
import { LockOverlay } from "./lock-overlay";

interface QuestCardProps {
  quest: TaskStatus;
  challengeTask: ChallengeTask | undefined;
  onClick: () => void;
  disabled?: boolean;
  isLocked?: boolean;
  unlockMessage?: string;
}

export const QuestCard = ({
  quest,
  challengeTask,
  onClick,
  disabled = false,
  isLocked = false,
  unlockMessage = "Locked",
}: QuestCardProps) => {
  const maxPoints = challengeTask ? getMaxPoints(challengeTask) : parseFloat(quest.maxPoints);
  const isCompleted = quest.status === ChallengeCampaignEventState.SUCCESSFUL;
  const isPending = quest.status === ChallengeCampaignEventState.PENDING;
  const shouldBeDisabled = disabled || isLocked;

  // Format points with no decimals
  const formattedPoints = Math.floor(maxPoints).toLocaleString();

  return (
    <div
      onClick={shouldBeDisabled ? undefined : onClick}
      className={cn(
        "relative bg-[#F1F3FF] group rounded-2xl overflow-hidden",
        shouldBeDisabled ? "cursor-not-allowed" : "cursor-pointer",
        isLocked && "opacity-70",
        !shouldBeDisabled &&
          "transition-all duration-200 ease-out hover:-translate-y-1",
        !shouldBeDisabled && "active:scale-[0.98]"
      )}
    >
      {/* Quest Image - Full Background with Padding */}
      <div className="relative w-full h-48 p-1 rounded-2xl overflow-hidden">
        {quest.media?.lightModeImageUrl ? (
          <img
            src={quest.media.lightModeImageUrl}
            alt={quest.title}
            className="w-full h-full object-cover rounded-xl"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-swapper-primary to-purple-600 rounded-xl">
            <QuestLogo size={64} />
          </div>
        )}
      </div>

      {/* Quest Content */}
      <div className="p-4">
        {/* Points & Category */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-swapper-primary font-bold text-sm">
              {formattedPoints}
            </span>
            <QuestLogo size={16} />
          </div>
          <span className={cn(
            "text-xs uppercase tracking-wider font-semibold",
            isCompleted && "text-green-500",
            isPending && "text-yellow-500",
            !isCompleted && !isPending && "text-swapper-primary"
          )}>
            {isCompleted ? "Completed" : isPending ? "Pending" : "Active"}
          </span>
        </div>

        {/* Title and Arrow */}
        <div className="flex flex-row items-center justify-between gap-2">
          <h3 className="text-swapper-secondary font-bold text-base line-clamp-2 flex-1 group-hover:text-swapper-primary transition-colors">
            {quest.title}
          </h3>
          <ArrowRight className="w-5 h-5 text-swapper-secondary flex-shrink-0 group-hover:translate-x-1 group-hover:text-swapper-primary transition-all" />
        </div>
      </div>

      {/* Lock Overlay */}
      {isLocked && <LockOverlay unlockMessage={unlockMessage} />}
    </div>
  );
};
